import { useCallback, useReducer } from "react";
import { IPoint } from "../../common/models/Point";
import { ChartIdentifier } from "../../services/trending-data-service/Models";

const tipDefaultMaxWidth = 500;
const defaultChartInstances = () => new Map<ChartIdentifier, Highcharts.Chart>();
const defaultHiddenSeries = () => new Map<number, Array<string>>();

export type GraphStateType = {

    allowAxesToggle: boolean;
    captureMouseMove: boolean;
    chartInstances: Map<ChartIdentifier, Highcharts.Chart>;
    graphHeight: string | undefined;
    graphParent: HTMLElement | undefined;
    hiddenSeries: Map<number, Array<string>>;
    showAxes: boolean;
    tipMaxWidth: number;
    tipOpen: boolean;
    tipPosition: IPoint | undefined;
    tipText: string | undefined;
};

let defaultGraphState: GraphStateType = {

    allowAxesToggle: false,
    captureMouseMove: false,
    chartInstances: defaultChartInstances(),
    graphHeight: undefined,
    graphParent: undefined,
    hiddenSeries: defaultHiddenSeries(),
    showAxes: true,
    tipMaxWidth: tipDefaultMaxWidth,
    tipOpen: false,
    tipPosition: undefined,
    tipText: undefined
};

enum GraphStateActionType {

    AllowAxesToggleChanged = 'GRAPH_ALLOW_AXES_TOGGLE_CHANGED',
    CaptureMouseMoveChanged = 'GRAPH_CAPTURE_MOUSE_MOVE_CHANGED',
    ChartInstancesChanged = 'GRAPH_CHART_INSTANCES_CHANGED',
    GraphHeightChanged = 'GRAPH_HEIGHT_CHANGED',
    GraphParentChanged = 'GRAPH_PARENT_CHANGED',
    HiddenSeriesChanged = 'GRAPH_HIDDEN_SERIES_CHANGED',
    ShowAxesChanged = 'GRAPH_SHOW_AXES_CHANGED',
    TipMaxWidthChanged = 'GRAPH_TIP_MAX_WIDTH_CHANGED',
    TipOpenChanged = 'GRAPH_TOP_OPEN_CHANGED',
    TipPositionChanged = 'GRAPH_TIP_POSITION_CHANGED',
    TipTextChanged = 'GRAPH_TIP_TEXT_CHANGED',
    Reset = 'GRAPH_RESET'
}

type GraphStateActions = | {
    type: GraphStateActionType.AllowAxesToggleChanged;
    payload: boolean;
} | {
    type: GraphStateActionType.CaptureMouseMoveChanged;
    payload: boolean;
} | {
    type: GraphStateActionType.ChartInstancesChanged;
    payload: Map<ChartIdentifier, Highcharts.Chart>;
} | {
    type: GraphStateActionType.GraphHeightChanged;
    payload: string | undefined;
} | {
    type: GraphStateActionType.GraphParentChanged;
    payload: HTMLElement | undefined;
} | {
    type: GraphStateActionType.HiddenSeriesChanged;
    payload: Map<number, Array<string>>;
} | {
    type: GraphStateActionType.ShowAxesChanged;
    payload: boolean;
} | {
    type: GraphStateActionType.TipMaxWidthChanged;
    payload: number;
} | {
    type: GraphStateActionType.TipOpenChanged;
    payload: boolean;
} | {
    type: GraphStateActionType.TipPositionChanged;
    payload: IPoint | undefined;
} | {
    type: GraphStateActionType.TipTextChanged;
    payload: string | undefined;
} | {
    type: GraphStateActionType.Reset;
    payload: void;
};

function graphReducer(state: GraphStateType, action: GraphStateActions): GraphStateType {

    const copy = { ...state };
    switch (action.type) {

        case GraphStateActionType.AllowAxesToggleChanged:
            copy.allowAxesToggle = action.payload;
            return copy;

        case GraphStateActionType.CaptureMouseMoveChanged:
            copy.captureMouseMove = action.payload;
            return copy;

        case GraphStateActionType.ChartInstancesChanged:
            copy.chartInstances = action.payload;
            return copy;

        case GraphStateActionType.GraphHeightChanged:
            copy.graphHeight = action.payload;
            return copy;

        case GraphStateActionType.GraphParentChanged:
            copy.graphParent = action.payload;
            return copy;

        case GraphStateActionType.HiddenSeriesChanged:
            copy.hiddenSeries = action.payload;
            return copy;

        case GraphStateActionType.ShowAxesChanged:
            copy.showAxes = action.payload;
            return copy;

        case GraphStateActionType.TipMaxWidthChanged:
            copy.tipMaxWidth = action.payload;
            return copy;

        case GraphStateActionType.TipOpenChanged:
            copy.tipOpen = action.payload;
            return copy;

        case GraphStateActionType.TipPositionChanged:
            copy.tipPosition = action.payload;
            return copy;

        case GraphStateActionType.TipTextChanged:
            copy.tipText = action.payload;
            return copy;

        case GraphStateActionType.Reset:
            return {
                ...defaultGraphState,
                // Keep the graphParent because it does not always get redrawn
                //  and set later.
                graphParent: copy.graphParent
            }

        default:
            return state;
    }
}

export default function useGraphState() {

    const [state, dispatch] = useReducer(graphReducer, {
        ...defaultGraphState
    });

    const setAllowAxesToggle = useCallback((value: boolean) => dispatch({
            type: GraphStateActionType.AllowAxesToggleChanged,
            payload: value
        }),
        []);


    const setCaptureMouseMove = useCallback((value: boolean) => dispatch({
            type: GraphStateActionType.CaptureMouseMoveChanged,
            payload: value
        }),
        []);

    const setGraphHeight = useCallback((value: string | undefined) => dispatch({
            type: GraphStateActionType.GraphHeightChanged,
            payload: value
        }),
        []);

    const setGraphParent = useCallback((value: HTMLElement | undefined) => dispatch({
            type: GraphStateActionType.GraphParentChanged,
            payload: value
        }),
        []);

    const setShowAxes = useCallback((value: boolean) => dispatch({
            type: GraphStateActionType.ShowAxesChanged,
            payload: value
        }),
        []);

    const setTipMaxWidth = useCallback((value: number) => dispatch({
            type: GraphStateActionType.TipMaxWidthChanged,
            payload: value
        }), []);

    const setTipOpen = useCallback((value: boolean) => dispatch({
            type: GraphStateActionType.TipOpenChanged,
            payload: value
        }), []);

    const setTipPosition = useCallback((value: IPoint) => dispatch({
            type: GraphStateActionType.TipPositionChanged,
            payload: value
        }),
        []);

    const setTipText = useCallback((value: string) => dispatch({
            type: GraphStateActionType.TipTextChanged,
            payload: value
        }),
        []);

    const resetState = useCallback(() => dispatch({
            type: GraphStateActionType.Reset,
            payload: undefined
        }), []);

    return {
        state,
        setAllowAxesToggle,
        setCaptureMouseMove,
        setGraphHeight,
        setGraphParent,
        setShowAxes,
        setTipMaxWidth,
        setTipOpen,
        setTipPosition,
        setTipText,
        resetState
    };
}

